import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { StatusBadge } from '@/components/common';
import type { ColDef } from '@/grid/types';
import {
  isOnFarmImplantEmbryoActionable,
  type OnFarmImplantSessionEmbryo,
} from '@/services/on-farm-implant';

export const statusColors = {
  Selected: { bg: 'bg-gray-100', text: 'text-gray-700' },
  Loaded: { bg: 'bg-blue-100', text: 'text-blue-700' },
  Queued: { bg: 'bg-green-100', text: 'text-green-700' },
  Discarded: { bg: 'bg-red-100', text: 'text-red-700' },
  Frozen: { bg: 'bg-cyan-100', text: 'text-cyan-700' },
  'Thawed and Discarded': { bg: 'bg-red-100', text: 'text-red-700' },
  'On-Farm Freezing': { bg: 'bg-sky-100', text: 'text-sky-700' },
  Unknown: { bg: 'bg-gray-100', text: 'text-gray-700' },
};

export const embryoStatusColors = {
  Frozen: { bg: 'bg-cyan-100', text: 'text-cyan-700' },
  Implanted: { bg: 'bg-indigo-100', text: 'text-indigo-700' },
  Discarded: { bg: 'bg-red-100', text: 'text-red-700' },
  Shipped: { bg: 'bg-amber-100', text: 'text-amber-700' },
  Used: { bg: 'bg-gray-100', text: 'text-gray-700' },
  'Thawed and Discarded': { bg: 'bg-red-100', text: 'text-red-700' },
  AI: { bg: 'bg-purple-100', text: 'text-purple-700' },
  'Per Preg Pricing Hold': { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  Missing: { bg: 'bg-orange-100', text: 'text-orange-700' },
  Unknown: { bg: 'bg-gray-100', text: 'text-gray-700' },
};

export const useFrozenEmbryoColumnDefs = (
  readOnly: boolean,
): ColDef<OnFarmImplantSessionEmbryo>[] => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      {
        colId: 'checkbox',
        headerName: '',
        width: 44,
        maxWidth: 44,
        checkboxSelection: !readOnly,
        headerCheckboxSelection: !readOnly,
        headerCheckboxSelectionFilteredOnly: true,
        suppressMovable: true,
        lockPosition: true,
        filter: false,
        sortable: false,
      },
      {
        field: 'embryoName',
        headerName: t('onFarmImplant.frozen.embryo'),
        flex: 1.4,
        minWidth: 150,
        filter: 'agTextColumnFilter',
        cellRenderer: (params: { data?: OnFarmImplantSessionEmbryo }) => {
          if (!params.data) return null;
          return (
            <div>
              <div className="font-medium text-gray-900">
                {params.data.embryoName ?? '\u2014'}
              </div>
              <div className="text-xs text-gray-500">
                {params.data.recordName ?? ''}
              </div>
            </div>
          );
        },
      },
      {
        field: 'accountName',
        headerName: t('onFarmImplant.frozen.account'),
        flex: 1.2,
        minWidth: 130,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'frozenStrawNumber',
        headerName: t('onFarmImplant.frozen.frozenStraw'),
        flex: 0.8,
        minWidth: 110,
        filter: 'agNumberColumnFilter',
        cellRenderer: (params: { data?: OnFarmImplantSessionEmbryo }) => {
          if (!params.data) return null;
          const { frozenStrawNumber, frozenStrawLabel } = params.data;
          return (
            <div>
              <div className="font-medium">{frozenStrawNumber ?? '\u2014'}</div>
              {frozenStrawLabel ? (
                <div className="text-xs text-gray-500">{frozenStrawLabel}</div>
              ) : null}
            </div>
          );
        },
      },
      {
        field: 'transferStrawNumber',
        headerName: t('onFarmImplant.frozen.transferStraw'),
        flex: 0.8,
        minWidth: 120,
        filter: 'agNumberColumnFilter',
        cellRenderer: (params: { data?: OnFarmImplantSessionEmbryo }) => {
          if (!params.data) return null;
          if (!params.data.transferStrawAssigned) {
            return (
              <span className="text-xs text-amber-600 italic">
                {t('onFarmImplant.frozen.transferStrawPending')}
              </span>
            );
          }
          return (
            <span className="font-medium">
              {params.data.transferStrawNumber}
            </span>
          );
        },
      },
      {
        field: 'cane',
        headerName: t('onFarmImplant.frozen.cane'),
        flex: 0.6,
        minWidth: 80,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'freezeMethodName',
        headerName: t('onFarmImplant.frozen.freezeMethod'),
        flex: 1,
        minWidth: 130,
        filter: 'agTextColumnFilter',
        cellRenderer: (params: { data?: OnFarmImplantSessionEmbryo }) => {
          if (!params.data) return null;
          if (params.data.freezeMethodId) {
            return <span>{params.data.freezeMethodName ?? '—'}</span>;
          }
          return (
            <a
              href={`/embryo/embryo?id=${params.data.embryoId}&tab=freezing`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => event.stopPropagation()}
              title={t('onFarmImplant.frozen.missingFreezeMethodHint')}
              aria-label={t('onFarmImplant.frozen.missingFreezeMethodFix', {
                embryo: params.data.embryoName ?? params.data.embryoId,
              })}
              className="inline-flex items-center gap-1 text-xs text-amber-700 underline"
            >
              <i className="pi pi-exclamation-triangle text-xs" />
              {t('onFarmImplant.frozen.missingFreezeMethod')}
            </a>
          );
        },
      },
      {
        field: 'statusName',
        headerName: t('onFarmImplant.frozen.status'),
        flex: 0.8,
        minWidth: 100,
        filter: 'agTextColumnFilter',
        cellRenderer: (params: { value?: string }) => {
          if (!params.value) return null;
          return <StatusBadge label={params.value} colorMap={statusColors} />;
        },
      },
      {
        field: 'embryoStatusName',
        headerName: t('onFarmImplant.frozen.embryoStatus'),
        flex: 0.9,
        minWidth: 120,
        filter: 'agTextColumnFilter',
        cellRenderer: (params: { value?: string }) => {
          if (!params.value) return null;
          return (
            <StatusBadge label={params.value} colorMap={embryoStatusColors} />
          );
        },
      },
    ],
    [readOnly, t],
  );
};

export const isFrozenEmbryoSelectable = (
  embryo: OnFarmImplantSessionEmbryo,
): boolean => isOnFarmImplantEmbryoActionable(embryo);
